import { useEffect, useRef } from 'preact/hooks';
import type { GameView } from '../engine';
import { sounds } from './audio';
import type { GameClient } from './game-client';

/** Cards played so far this hand, counting the trick on the table. */
function playedCount(view: GameView): number {
  return view.hand.won.flat().length + (view.hand.trick?.plays.length ?? 0);
}

function onBidderSide(view: GameView, seat: number): boolean {
  return seat === view.hand.bidder || seat === view.hand.partner;
}

export function myMove(client: GameClient): boolean {
  const { view, seat, legal } = client;
  if (seat === null || view.phase.kind === 'gameOver') return false;
  if (legal.length > 0) return true;
  if (view.phase.kind === 'trim') return !!view.phase.pending[seat];
  return view.phase.kind === 'pass' && view.hand.bidder === seat;
}

/**
 * Plays sounds by diffing consecutive views (Hexagon's approach), so hot-seat
 * and online sound the same without the engine knowing about audio.
 */
export function useGameSounds(client: GameClient): void {
  const prev = useRef<{ view: GameView; mine: boolean } | null>(null);
  const { view, seat } = client;
  // Hot-seat too: with computers playing, the screen alone was easy to miss.
  const mine = myMove(client);

  useEffect(() => {
    const before = prev.current;
    prev.current = { view, mine };
    if (!before) return;
    const b = before.view;

    if (view.history.length > b.history.length) {
      const result = view.history[view.history.length - 1]!;
      // Spectators hear it from the bidder's side.
      const ours = seat === null || seat === result.bidder || seat === result.partner;
      if (result.made === ours) sounds.made();
      else sounds.set();
    } else if (view.hand.number === b.hand.number) {
      const trickDone = view.hand.lastTrick && view.hand.won.flat().length > b.hand.won.flat().length;
      if (trickDone) {
        const winner = view.hand.lastTrick!.winner;
        sounds.trickWon(seat === null || onBidderSide(view, winner) === onBidderSide(view, seat));
      } else if (playedCount(view) > playedCount(b)) {
        sounds.play();
      }
      if (b.hand.partner === 'unknown' && view.hand.partner !== 'unknown' && view.hand.called) sounds.partner();
    }
    if (view.phase.kind === 'gameOver' && b.phase.kind !== 'gameOver') setTimeout(() => sounds.win(), 600);

    if (mine && !before.mine) {
      sounds.yourTurn();
      try {
        navigator.vibrate?.(120);
      } catch {
        // Not supported; the chime is enough.
      }
    }
  }, [view, mine]);
}
